<template>
  <basic-dialog :show="show" @close="$emit('close')" maxWidth="800">
    <template v-slot:title>{{ serviceName }}</template>
    <v-row>
      <v-col cols="8">
        <dates-range-picker
          :gtDateFromString="startDate"
          :gtDateToString="endDate"
          :rangeStart="dateRangeStart"
          :rangeEnd="dateRangeEnd"
          @onRangeSelected="onDateRangeSelected"
          :outlined="$store.getters.localSettings('outlinedInputs')"
        ></dates-range-picker>
      </v-col>
      <v-col v-if="showSearchDateRangeButton">
        <v-btn
          :disabled="this.rateBandsLoading"
          color="primary"
          @click="onLoadRateBands"
        >
          <v-progress-circular
            v-if="this.rateBandsLoading"
            indeterminate
            color="green"
            :width="2"
            :size="25"
          ></v-progress-circular>
          Search date range
        </v-btn>
      </v-col>
    </v-row>
    <v-row v-if="rateBands.length > 0">
      <v-col>
        <v-select
          :items="rateBands"
          v-model="selectedSRateBandId"
          label="Select Rate Band"
          item-text="name"
          item-value="code"
          @change="onRateBandChange($event)"
        ></v-select>
      </v-col>
    </v-row>
    <v-row v-if="showPricingInputs">
      <v-col>
        <v-text-field
          :outlined="$store.getters.localSettings('outlinedInputs')"
          hide-details
          label="Adults"
          type="number"
          v-model="adults"
          :max="maxAdults"
        />
      </v-col>
      <v-col>
        <v-text-field
          :outlined="$store.getters.localSettings('outlinedInputs')"
          hide-details
          label="Children"
          type="number"
          v-model="children"
          :max="maxChildren"
        />
      </v-col>
      <v-col v-if="!hideInfants">
        <v-text-field
          :outlined="$store.getters.localSettings('outlinedInputs')"
          hide-details
          label="Infants"
          type="number"
          v-model="infants"
          :max="maxInfants"
        />
      </v-col>
      <div v-if="selectedSRateBand && selectedSRateBand.groups">
        <v-col
          v-for="group in selectedSRateBand.groups.filter((g) => {
            return !g.code.toString().startsWith('RS');
          })"
          :key="group.code"
        >
          <v-text-field
            :outlined="$store.getters.localSettings('outlinedInputs')"
            hide-details
            :label="group.name"
            type="number"
            value="0"
            :min="0"
            @input="onSpecialRateChange($event, group.code)"
          />
        </v-col>
      </div>
      <v-col>
        <v-text-field
          :outlined="$store.getters.localSettings('outlinedInputs')"
          hide-details
          label="Qty"
          type="number"
          v-model="qty"
        />
      </v-col>
      <v-col cols="4">
        <service-status-list-select
          :service-status-id="status"
          :service-status-name="statusName"
          @onSelect="onServiceStatusChange"
          :outlined="$store.getters.localSettings('outlinedInputs')"
        ></service-status-list-select>
      </v-col>
    </v-row>
    <v-container class="pb-0 px-0" v-if="cancellationPolicy != ''">
      <v-alert type="info" transition="scale-transition"
        ><div v-html="cancellationPolicy"></div
      ></v-alert>
    </v-container>
    <item-linked-services-list
      :services-ids="rsResultServiceLinkedServices"
      :header-props="headerProps"
      :service-id="serviceGtId"
      :start-date="startDate"
      :end-date="endDate"
      :adults="headerProps.adults"
      :children="headerProps.children"
      :infants="headerProps.infants"
      :qty="headerProps.qty"
      :service-type="serviceType"
      :department="headerProps.departmentName"
      :agent-id="headerProps.agentId"
      :destination-id="headerProps.destinationId"
      :rate-plan="ratePlan"
      :item-linked-services="linkedServices"
      @save="onLinkedServiceSave"
      marketplace
    >
    </item-linked-services-list>

    <item-extras-list
      v-if="showPricingInputs"
      :service-id="serviceGtId"
      :rs-result-service-id="rsResultServiceId"
      :from="startDate"
      :to="endDate"
      :adults="adults"
      :children="children"
      :infants="infants"
      :qty="qty"
      :service-type="serviceType"
      @on-selected-extras-change="onSelectedExtrasChange"
      :extras="extras"
    ></item-extras-list>
    <v-container class="text-right pa-0 pt-3">
      <h6 class="headline green--text font-weight-light">
        <v-progress-circular
          v-if="this.$apollo.loading"
          indeterminate
          color="green"
          :width="2"
          :size="25"
        ></v-progress-circular>
        <small>Total Price:</small>
        <b>{{ totalPrice | priceLabeler }}</b>
        <small>{{ currency }}</small>
      </h6>
    </v-container>
    <v-alert
      :value="error"
      type="error"
      v-if="showPricingInputs && errorMsg.toString() != ''"
      >{{ errorMsg }}</v-alert
    >
    <template v-slot:actions>
      <v-btn color="primary" @click="onAddItem" :disabled="error">Add</v-btn>
    </template>
    <v-row v-if="debug">
      <v-col>
        <code>{{ rsResultServiceInfo }}</code>
        <v-divider />
      </v-col>
    </v-row>
  </basic-dialog>
</template>

<script>
import gql from "graphql-tag";
import BasicDialog from "../../../shared/BasicDialog.vue";
import DatesRangePicker from "../../../shared/DatesRangePicker.vue";
import ServiceStatusListSelect from "../../../shared/ServiceStatusListSelect.vue";

import ItemExtrasList from "../tools/ItemExtrasList.vue";
import ItemLinkedServicesList from "../tools/ItemLinkedServicesList.vue";
import { removeGQLTypename } from "../../../../../helpers";
const randomize = require("randomatic");
export const ITEM_RATE_BANDS = gql`
  query rsRateBands(
    $supplierId: ID!
    $serviceId: ID!
    $startDate: DateWithTimeType!
    $endDate: DateWithTimeType!
    $agentId: ID!
    $department: String!
    $provider: String!
    $qty: Int!
    $children: Int!
    $adults: Int!
  ) {
    rateBands: getRateBands(
      supplierId: $supplierId
      serviceId: $serviceId
      provider: $provider
      department: $department
      agentId: $agentId
      startDate: $startDate
      endDate: $endDate
      qty: $qty
      children: $children
      adults: $adults
    ) {
      name
      code
      currency
      agentCurrency
      price
      agentPrice
      groups {
        name
        code
        abbv
      }
    }
  }
`;

export const ITEM_PRICING = gql`
  query rsResultServicePricing(
    $serviceId: ID!
    $rsResultServiceId: ID
    $startDate: DateWithTimeType!
    $endDate: DateWithTimeType!
    $adults: Int!
    $children: Int!
    $infants: Int!
    $qty: Int!
    $agentId: ID
    $department: String
    $ratePlan: String
    $rateCodes: String
    $extras: [ItineraryItemExtraInput]
    $yearShift: Int
  ) {
    itemPricing: getPricing(
      serviceId: $serviceId
      rsResultServiceId: $rsResultServiceId
      agentId: $agentId
      department: $department
      ratePlan: $ratePlan
      rateCodes: $rateCodes
      adults: $adults
      children: $children
      infants: $infants
      qty: $qty
      startDate: $startDate
      endDate: $endDate
      extras: $extras
      yearShift: $yearShift
    ) {
      adultBuy
      adultSell
      childBuy
      childSell
      infantBuy
      infantSell
      itemBuy
      itemSell
      rateTypeCode
      ratePlan
      currencySell
      currencySellExchangeRate
      currencyBuy
      currencyBuyExchangeRate
      rateTypeCode
      totalSell
      serviceSell
      discount
      businessModel
      supplierCommission
      message
      extras {
        id
        name
        uid
        totalSell
        totalBuy
        isChildOnly
        isInfantOnly
        rateTypeCode
        rateBase
        adults
        children
        infants
        cost
        sell
        percentPrice
        customPricing
        isMandatory
        discount
        discountCost
        discountSell
        currencyBuyExchangeRate
        currencySellExchangeRate
      }
    }
  }
`;
const RS_RESULT_SERVICE_INFO = gql`
  query rsResultServiceInfo($id: ID!) {
    rsResultServiceInfo: rsResultService(id: $id) {
      id
      description
      cancellationPolicy
      gtService {
        id
        name
        marketPlace
        pickupPoint
        dropoffPoint
        pickupTime
        dropoffTime
        linkedServices {
          edges {
            node {
              id
              name
              marketPlace
            }
          }
        }
      }
    }
  }
`;
export default {
  name: "rate-search-service-details",

  props: {
    value: {
      type: Object,
      default: () => null,
    },
    show: {
      type: Boolean,
      default: () => false,
    },
  },

  components: {
    BasicDialog,
    DatesRangePicker,
    ServiceStatusListSelect,
    ItemExtrasList,
    ItemLinkedServicesList,
  },

  data() {
    return {
      debug: false,
      startDate: this.value ? this.value.search.startDate : "",
      endDate: this.value ? this.value.search.endDate : "",
      dateRangeStart: this.value ? this.value.search.startDate : "",
      dateRangeEnd: this.value ? this.value.search.endDate : "",
      adults: this.value ? this.value.search.adults : 2,
      children: this.value ? this.value.search.children : 0,
      infants: this.value ? this.value.search.infants : 0,
      qty: 1,
      status: "",
      statusName: "Quotation",
      errorMsg: "",
      serviceStandardOccupancy: 999,
      ratePlan: "",
      extras: [],
      rateBands: [],
      selectedSRateBandId: "",
      selectedSRateBand: { groups: [] },
      specialRates: {},
      specialRateCodes: "",
      rateBandsLoading: false,
      linkedServices: [],
      remarks: {
        pickupPoint: "",
        dropoffPoint: "",
        pickupTime: "",
        dropoffTime: "",
        timeFrom: "",
        agentNotes: "",
        supplierNotes: "",
        voucherNotes: "",
        internalNotes: "",
        flightNo: "",
        confirmationNo: "",
        roomNumbers: "",
      },
    };
  },

  computed: {
    cancellationPolicy() {
      return this.rsResultServiceInfo &&
        this.rsResultServiceInfo.cancellationPolicy
        ? this.rsResultServiceInfo.cancellationPolicy
        : "";
    },
    linkedServicesTotalPrice() {
      return this.linkedServices
        ? this.linkedServices.reduce(
            (acc, item) =>
              acc + (item.hasOwnProperty("price") ? Number(item.price) : 0),
            0
          )
        : 0;
    },
    rsResultServiceLinkedServices() {
      return this.rsResultServiceInfo && this.rsResultServiceInfo.gtService
        ? this.rsResultServiceInfo.gtService.linkedServices.edges
            .filter((e) => e.node.marketPlace)
            .map((e) => e.node.id)
        : [];
    },
    headerProps() {
      return {
        departmentName:
          this.value && this.value.search ? this.value.search.department : "",
        agentId:
          this.value && this.value.search ? this.value.search.agentId : "",
        destinationId:
          this.value && this.value.search
            ? this.value.search.destination.id
            : "",
        startDate: this.startDate,
        endDate: this.endDate,
        adults: this.adults.toString(),
        children:
          this.value && this.value.search
            ? this.value.search.children.toString()
            : "0",
        infants:
          this.value && this.value.search
            ? this.value.search.infants.toString()
            : "0",
        qty: this.qty,
      };
    },
    showSearchDateRangeButton() {
      return (
        this.value &&
        this.value.supplier &&
        this.value.supplier.provider == "RESREQUEST"
      );
    },
    sourceId() {
      if (
        this.value &&
        this.value.supplier &&
        this.value.supplier.provider == "RESREQUEST"
      ) {
        return `${this.serviceGtId}:::${this.selectedSRateBandId}`;
      }
      return this.value ? this.value.service.providerId : "";
    },
    showPricingInputs() {
      if (
        this.value &&
        this.value.supplier &&
        this.value.supplier.provider == "RESREQUEST"
      ) {
        return this.selectedSRateBandId != "";
      }
      return true;
    },
    maxAdults() {
      return this.value ? this.value.search.adults : 2;
    },
    maxChildren() {
      return this.value ? this.value.search.children : 0;
    },
    maxInfants() {
      return this.value ? this.value.search.infants : 0;
    },
    serviceGtId() {
      return this.value ? this.value.service.gtId : "";
    },
    rsResultServiceId() {
      return this.value ? this.value.service.id : null;
    },
    serviceType() {
      return this.value && this.value.supplier && this.value.supplier.gtSupplier
        ? this.value.supplier.gtSupplier.serviceType
        : "ACCOMMODATION";
    },
    customPricing() {
      return this.value && this.value.supplier.provider != "GTWEB";
    },
    hideInfants() {
      return this.value && this.value.supplier.provider == "RESREQUEST";
    },
    itineraryItem() {
      const itemId = randomize("Aa0", 32);
      if (!this.itemPricing) {
        return {};
      } else {
        let adtSupplement = 0;
        let chdSupplement = 0;
        let qtySupplement = 0;
        if (this.specialRates) {
          Object.keys(this.specialRates).forEach((key) => {
            const v = Number(this.specialRates[key]);
            if (v > 0) {
              const [_, rClass] = key.split("_");
              if (rClass == "0") {
                qtySupplement += v;
              } else if (rClass == "1") {
                adtSupplement += v;
              } else if (rClass == "2") {
                chdSupplement += v;
              }
            }
          });
        }

        //console.log("his.source.name",this.value.supplier.provider, this.specialRateCodes)
        const ratePlan =
          this.value && this.value.supplier.provider == "RESREQUEST"
            ? this.specialRateCodes
            : this.ratePlan;
        const item = {
          id: itemId,
          serviceId: this.serviceGtId,
          serviceName: this.value
            ? this.value.service.serviceName
            : "New Service",
          supplierName: this.value
            ? this.value.supplier.supplierName
            : "New Supplier",
          supplierId: this.value ? this.value.supplier.id : undefined,
          startDate: this.startDate,
          endDate: this.endDate,
          price: this.itemPricing ? this.itemPricing.totalSell : 0,
          status: this.statusName,
          statusId: this.status != "" ? this.status : "U2VydmljZVN0YXR1cy0x",
          adults: Number(this.adults) + adtSupplement,
          children: Number(this.children) + chdSupplement,
          infants: this.infants,
          qty: Number(this.qty) + qtySupplement,
          stdOccupancy: this.serviceStandardOccupancy,
          rateTypeCode: this.itemPricing ? this.itemPricing.rateTypeCode : "",
          ratePlan: ratePlan,
          serviceType: this.serviceType,
          selected: false,
          customPricing: this.customPricing,
          isOptional: false,
          isIncluded: true,
          includeCommission: true,
          businessModel: "",
          boardBasis: "",
          supplierCommission: 0,
          source: {
            name: this.value ? this.value.supplier.provider : "GTWEB",
            id: this.sourceId,
            rsResultServiceId: this.rsResultServiceId,
          },
          remarks: { ...this.remarks },
          pricing: {
            adultBuy: this.itemPricing ? Number(this.itemPricing.adultBuy) : 0,
            adultSell: this.itemPricing
              ? Number(this.itemPricing.adultSell)
              : 0,
            childBuy: this.itemPricing ? Number(this.itemPricing.childBuy) : 0,
            childSell: this.itemPricing
              ? Number(this.itemPricing.childSell)
              : 0,
            infantBuy: this.itemPricing
              ? Number(this.itemPricing.infantBuy)
              : 0,
            infantSell: this.itemPricing
              ? Number(this.itemPricing.infantSell)
              : 0,
            itemBuy: this.itemPricing ? Number(this.itemPricing.itemBuy) : 0,
            itemSell: this.itemPricing ? Number(this.itemPricing.itemSell) : 0,
            rateTypeCode: this.itemPricing ? this.itemPricing.rateTypeCode : "",
            currencyBuy: this.itemPricing ? this.itemPricing.currencyBuy : "",
            currencyBuyExchangeRate: this.itemPricing
              ? this.itemPricing.currencyBuyExchangeRate
              : 1,
            currencySell: this.itemPricing ? this.itemPricing.currencySell : "",
            currencySellExchangeRate: this.itemPricing
              ? this.itemPricing.currencySellExchangeRate
              : "",
          },
          extras:
            this.selectedExtras.length > 0
              ? [...this.selectedExtras]
              : undefined,
          yearShift: undefined, //for now
          linkedServices: this.linkedServices.map((ls) => ({
            ...ls,
            parentId: itemId,
            customPricing: this.customPricing,
            source: {
              name: this.value ? this.value.supplier.provider : "GTWEB",
              id: this.sourceId,
            },
          })),
        };
        return item;
      }
    },
    selectedExtras() {
      //console.log("updatedExtrasInfo", this.extras, this.itemPricing);
      return [
        ...this.extras.map((e) => {
          if (!e.statusId) {
            e.statusId = "U2VydmljZVN0YXR1cy0x";
            e.status = "Quotation";
          }
          const extraPricingInfo =
            this.itemPricing && this.itemPricing.extras
              ? this.itemPricing.extras.find((pe) => {
                  return pe.id == e.id;
                })
              : undefined;
          //update qty based on rateBase
          let extraQty = e.qty;
          if (extraPricingInfo && extraPricingInfo.rateBase == "P") {
            extraQty = 1;
          }
          //console.log("extraPricingInfo", extraPricingInfo, this.itemPricing);
          return extraPricingInfo
            ? {
                ...e,
                qty: extraQty,
                totalSell: extraPricingInfo.totalSell,
                isChildOnly: extraPricingInfo.isChildOnly,
                isInfantOnly: extraPricingInfo.isInfantOnly,
                isMandatory: extraPricingInfo.isMandatory,
                rateTypeCode: extraPricingInfo.rateTypeCode,
                rateBase: extraPricingInfo.rateBase,
                adults: e.adults,
                children: e.children,
                infants: e.infants,
                totalBuy: extraPricingInfo.totalBuy,
                cost: extraPricingInfo.cost,
                sell: extraPricingInfo.sell,
                percentPrice: extraPricingInfo.percentPrice,
                customPricing: this.customPricing,
                isOptional: false,
                isIncluded: true,
                pricing: this.customPricing
                  ? {
                      //taken from parent service
                      currency: this.itemPricing.currencyBuy,
                      currencyExchangeRate:
                        this.itemPricing.currencyBuyExchangeRate,
                      cost: extraPricingInfo.cost,
                      sell: extraPricingInfo.sell,
                      rateTypeCode: extraPricingInfo.rateTypeCode,
                    }
                  : undefined,
              }
            : {
                //custom pricing extra
                ...e,
                totalSell:
                  Number(e.rateBase == "I" ? e.qty : e.adults) * Number(e.sell),
                adults: e.adults,
                children: e.children,
                infants: e.infants,
                cost: e.buy,
                sell: e.sell,
                customPricing: this.customPricing,
                isOptional: false,
                isIncluded: true,
                pricing: this.customPricing
                  ? {
                      //taken from parent service
                      // should be buy currency
                      currency: e.currencyBuy,
                      currencyExchangeRate: e.currencyBuyExchange,
                      cost: e.buy,
                      sell: e.sell,
                      rateTypeCode: e.rateTypeCode,
                    }
                  : undefined,
              };
        }),
      ];
    },
    error() {
      return !this.itemPricing;
    },
    totalPrice() {
      return this.itemPricing
        ? this.itemPricing.totalSell + this.linkedServicesTotalPrice
        : 0;
    },
    currency() {
      return this.itemPricing ? this.itemPricing.currencySell : "";
    },
    serviceName() {
      return this.value ? this.value.service.serviceName : "Service Details";
    },
    pricingParams() {
      return this.value
        ? {
            serviceId: this.value.service.gtId,
            rsResultServiceId: this.value.service.id,
            agentId: this.value.search.agentId,
            department: this.value.search.department,
            ratePlan: this.selectedSRateBandId,
            rateCodes: this.specialRateCodes,
            adults: parseInt(this.adults),
            children: parseInt(this.children),
            infants: parseInt(this.infants),
            qty: parseInt(this.qty),
            startDate: this.startDate,
            endDate: this.endDate,
            extras: this.extras.map((e) => {
              let extraQty = e.qty;
              if (e.rateBase == "P") {
                if (e.isChildOnly && e.children) {
                  extraQty = Number(e.children);
                } else if (e.isInfantOnly && e.infants) {
                  extraQty = Number(e.infants);
                } else if (e.adults) {
                  extraQty = Number(e.adults);
                }
              }
              return {
                id: e.id,
                qty: extraQty,
                startDate: e.startDate,
                endDate: e.endDate,
              };
            }),
            yearShift: null,
          }
        : null;
    },
  },

  watch: {
    value(newVal) {
      if (newVal) {
        this.startDate = newVal.search.startDate;
        this.endDate = newVal.search.endDate;
        this.dateRangeStart = newVal.search.startDate;
        this.dateRangeEnd = newVal.search.endDate;
        this.adults = newVal.search.adults;
        this.children = newVal.search.children;
        this.infants = newVal.search.infants;
      }
      this.extras = [];
    },
  },

  methods: {
    onLinkedServiceSave(item) {
      console.log("onLinkedServiceSave", item);
      //check if item is already in linkedServices
      const index = this.linkedServices.findIndex(
        (service) => service.serviceId === item.serviceId
      );
      if (index > -1) {
        this.linkedServices[index] = { ...item };
      } else {
        this.linkedServices.push(item);
      }
    },
    onSpecialRateChange(event, code) {
      console.log("onSpecialRateChange", event, code);
      this.specialRates[code] = event;
      this.specialRateCodes = Object.keys(this.specialRates)
        .map((key) => {
          return `${key}=${this.specialRates[key]}`;
        })
        .join(";");
      this.$apollo.queries.itemPricing.refetch();
    },
    onRateBandChange(event) {
      console.log("onRateBandChange", event);
      this.selectedSRateBand = this.rateBands.find((rb) => {
        return rb.code == event;
      });
      console.log("this.selectedSRateBand", this.selectedSRateBand);
    },
    onLoadRateBands() {
      console.log("onLoadRateBands", this.value);
      this.rateBandsLoading = true;
      const v = {
        supplierId: this.value.supplier.gtSupplier.id,
        serviceId: this.value.service.gtId,
        provider: this.value.supplier.provider,
        department: this.value.search.department,
        agentId: this.value.search.agentId,
        startDate: this.startDate,
        endDate: this.endDate,
        qty: this.qty,
        children: this.children,
        adults: this.adults,
      };
      console.log("this. this.value", v);
      this.$apollo
        .query({
          query: ITEM_RATE_BANDS,
          variables: v,
        })
        .then((res) => {
          this.rateBands = res.data.rateBands;
          console.log("rate bands", res.data.rateBands);
        })
        .finally(() => {
          this.rateBandsLoading = false;
        });
    },
    onSelectedExtrasChange(payload) {
      this.extras = [...payload];
    },
    onAddItem() {
      //console.log("this.itineraryItem",this.itineraryItem)
      this.$emit("add", { ...this.itineraryItem });
    },
    onServiceStatusChange(payload) {
      this.status = payload.id;
      this.statusName = payload.name;
    },
    onDateRangeSelected(payload) {
      this.startDate = payload.dateFrom;
      this.endDate = payload.dateTo;
    },
  },

  apollo: {
    itemPricing: {
      query: ITEM_PRICING,
      variables() {
        return { ...this.pricingParams };
      },
      skip() {
        if (!this.value || !this.value.supplier) {
          return true;
        }
        if (
          this.show &&
          this.value.supplier.provider == "RESREQUEST" &&
          this.selectedSRateBandId == ""
        ) {
          return true;
        }
        return this.pricingParams == null || !this.show;
      },
      error(err) {
        this.errorMsg = err.graphQLErrors.map((e) => e.message).join(", ");
      },
      fetchPolicy: "network-only",
    },
    rsResultServiceInfo: {
      query: RS_RESULT_SERVICE_INFO,
      variables() {
        return { id: this.rsResultServiceId };
      },
      skip() {
        return (
          !this.rsResultServiceId ||
          !this.show ||
          this.rsResultServiceId.toString() == ""
        );
      },
      result(result) {
        if (!result.data || !result.data.rsResultServiceInfo) return;
        const svc = result.data.rsResultServiceInfo.gtService;
        if (!svc) return;
        if (!this.remarks.pickupPoint)
          this.remarks.pickupPoint = svc.pickupPoint || "";
        if (!this.remarks.dropoffPoint)
          this.remarks.dropoffPoint = svc.dropoffPoint || "";
        if (!this.remarks.pickupTime)
          this.remarks.pickupTime = svc.pickupTime || "";
        if (!this.remarks.dropoffTime)
          this.remarks.dropoffTime = svc.dropoffTime || "";
      },
    },
  },
};
</script>

<style></style>



// WEBPACK FOOTER //
// src/components/gt/itineraries/builder/rates-search/RateSearchServiceDetails.vue